import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { challengeAPI, matchAPI } from '../lib/api';
import { Lightning, Clock, Check, X, Trophy, ArrowsClockwise, Warning, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusPill from '../components/StatusPill';
import MemberLink from '../components/MemberLink';
import { ChallengeCardSkeleton, SkeletonList } from '../components/Skeleton';
import { isWalkover, WALKOVER, formatScore } from '../lib/scoreFormat';

export default function ChallengesPage() {
  const [challenges, setChallenges] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitModal, setSubmitModal] = useState(null);
  // Pending confirmations: {action, id, label}
  const [confirmState, setConfirmState] = useState(null);
  // Per-action loading state. Key: `${kind}:${id}` (e.g. "accept:abc-123").
  // Used to (a) disable the clicked button + swap its label to a -ing form,
  // and (b) mark the surrounding card as "Updating…" while the next fetch
  // runs. Prevents duplicate taps and gives users immediate feedback.
  const [busyMap, setBusyMap] = useState({});
  // Cards keep showing the spinner during the refetch that follows a
  // successful action, since data refresh is the visually slow step.
  const [refreshingIds, setRefreshingIds] = useState({});
  const { user, player: myPlayer, hasLadderAccess, refreshUser } = useAuth();

  const setBusy = (key, on) =>
    setBusyMap(prev => {
      const next = { ...prev };
      if (on) next[key] = true; else delete next[key];
      return next;
    });
  const setRefreshing = (id, on) =>
    setRefreshingIds(prev => {
      const next = { ...prev };
      if (on) next[id] = true; else delete next[id];
      return next;
    });
  const isBusy = (key) => !!busyMap[key];
  const isCardRefreshing = (id) => !!refreshingIds[id];

  // Admins have no ladder player row, so the regular player Challenges page
  // would render empty and confusing. Route them to the admin-wide view
  // (AdminPage opens on its Challenges tab when `?tab=challenges`).
  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const [chRes, mRes] = await Promise.all([
        challengeAPI.getMy(),
        matchAPI.getMy(),
      ]);
      setChallenges(chRes.data.challenges);
      setMatches(mRes.data.matches);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAccept = async (id) => {
    const key = `accept:${id}`;
    if (isBusy(key)) return;
    setBusy(key, true);
    try {
      await challengeAPI.accept(id);
      toast.success('Challenge accepted!');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(key, false);
    }
  };

  const runDecline = async (id) => {
    const key = `decline:${id}`;
    if (isBusy(key)) return;
    setBusy(key, true);
    // Optimistic update — remove the declined challenge from local state
    // immediately so the user sees the change without waiting for the round trip.
    // Snapshot prior state to restore on failure.
    const priorChallenges = challenges;
    setChallenges(prev => prev.filter(c => c.challenge_id !== id));
    try {
      await challengeAPI.decline(id);
      toast.success('Challenge declined');
      await fetchData();
    } catch (err) {
      // Roll back the optimistic removal so the card reappears.
      setChallenges(priorChallenges);
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(key, false);
    }
  };

  const runCancel = async (id) => {
    const key = `cancel:${id}`;
    if (isBusy(key)) return;
    setBusy(key, true);
    const priorChallenges = challenges;
    setChallenges(prev => prev.filter(c => c.challenge_id !== id));
    try {
      await challengeAPI.cancel(id);
      toast.success('Challenge canceled');
      await fetchData();
    } catch (err) {
      setChallenges(priorChallenges);
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(key, false);
    }
  };

  const handleApprove = async (matchId) => {
    const key = `approve:${matchId}`;
    if (isBusy(key)) return;
    setBusy(key, true);
    try {
      await matchAPI.approve(matchId);
      toast.success('Match approved! Rankings updated.');
      // Rankings just shifted — sync cached AuthContext player so
      // the Ladder page and header show the correct rank next navigation.
      refreshUser?.();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(key, false);
    }
  };

  const handleDecline = (id) => setConfirmState({
    kind: 'decline', id,
    title: 'Decline challenge?',
    message: 'Are you sure you want to decline this challenge?',
    confirmLabel: 'Decline',
    destructive: true,
  });
  const handleCancel = (id) => setConfirmState({
    kind: 'cancel', id,
    title: 'Cancel challenge?',
    message: 'Are you sure you want to cancel this challenge?',
    confirmLabel: 'Cancel challenge',
    destructive: true,
  });
  // "Correct Score" flow: confirm first, then immediately open the score modal
  // pre-pointed at the right challenge so the reviewing player can submit a
  // corrected score in a single uninterrupted step.
  const handleCorrect = (challenge) => {
    const key = `correct:${challenge.challenge_id}`;
    if (isBusy(key)) return;
    setConfirmState({
      kind: 'correct', id: challenge.challenge_id, challenge,
      title: 'Correct submitted score?',
      message: 'Are you sure you want to correct this submitted score? Cancelling here leaves the existing score in place.',
      confirmLabel: 'Yes, correct score',
      destructive: false,
    });
  };

  const runConfirm = async () => {
    if (!confirmState) return;
    const { kind, id, challenge } = confirmState;
    setConfirmState(null);
    if (kind === 'decline') await runDecline(id);
    else if (kind === 'cancel') await runCancel(id);
    else if (kind === 'correct') {
      // Brief "Opening correction…" state so users see the click registered
      // even before the modal mounts. Cleared as soon as the modal opens.
      setBusy(`correct:${id}`, true);
      setSubmitModal({ ...challenge, isCorrection: true });
      // Modal is mounted synchronously in next render; clear on next tick.
      setTimeout(() => setBusy(`correct:${id}`, false), 50);
    }
  };

  if (isAdmin) {
    return <Navigate to="/admin?tab=challenges" replace />;
  }

  // Approved RTC member who is NOT on the Singles Ladder. Challenges are a
  // ladder-only feature, so show a friendly explainer instead of an empty
  // list with broken affordances.
  if (!hasLadderAccess) {
    return (
      <div
        className="max-w-2xl mx-auto px-4 py-6 space-y-5"
        data-testid="challenges-page-non-member"
      >
        <div>
          <h1 className="font-serif text-4xl text-[#013523] tracking-tight">
            Challenges
          </h1>
        </div>
        <div
          className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl px-5 py-6 space-y-3"
          data-testid="challenges-not-member-card"
        >
          <h2 className="font-serif text-xl text-[#013523]">
            You're not currently on the Singles Ladder
          </h2>
          <p className="text-sm text-[#6a766e]">
            Ask an admin to join the ladder if you want to challenge players. Challenge match
            play is opt-in and tied to the Men's Singles Ladder ranking.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    // Render the page shell immediately with skeleton cards so users see
    // structure and never a blank/frozen screen on tap-into navigation.
    return (
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6" data-testid="challenges-loading">
        <div>
          <h1 className="font-serif text-2xl text-[#013523] tracking-tight">Challenges</h1>
          <p className="text-xs text-[#6a766e] mt-1">Loading your matches…</p>
        </div>
        <SkeletonList count={3} Component={ChallengeCardSkeleton} />
      </div>
    );
  }

  // Build a map: challenge_id -> latest match state
  const matchByChallenge = {};
  for (const m of matches) {
    const existing = matchByChallenge[m.challenge_id];
    if (!existing || m.created_at > existing.created_at) {
      matchByChallenge[m.challenge_id] = m;
    }
  }

  const activeChallenges = challenges.filter(c => ['pending', 'accepted'].includes(c.status));
  const pastChallenges = challenges.filter(c => !['pending', 'accepted'].includes(c.status));

  // Separate accepted challenges by their match state
  const pendingScoreApproval = []; // has a pending match
  const rejectedScores = [];       // latest match is rejected — needs resubmission
  const awaitingScore = [];         // accepted but no pending/approved match — needs score entry
  const pendingChallenges = [];     // not yet accepted

  for (const ch of activeChallenges) {
    if (ch.status === 'pending') {
      pendingChallenges.push(ch);
      continue;
    }
    // ch.status === 'accepted'
    const latestMatch = matchByChallenge[ch.challenge_id];
    if (!latestMatch || latestMatch.approval_status === 'rejected') {
      if (latestMatch?.approval_status === 'rejected') {
        rejectedScores.push({ challenge: ch, match: latestMatch });
      } else {
        awaitingScore.push(ch);
      }
    } else if (latestMatch.approval_status === 'pending') {
      pendingScoreApproval.push({ challenge: ch, match: latestMatch });
    }
    // approved matches mean challenge is completed — should not be in activeChallenges
  }

  const hasActiveItems = pendingScoreApproval.length > 0 || rejectedScores.length > 0 || awaitingScore.length > 0 || pendingChallenges.length > 0;

  return (
    <div className="space-y-6" data-testid="challenges-page">
      <div>
        <h1
          data-testid="challenges-club-name"
          className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight"
        >
          Challenges
        </h1>
        <p className="text-sm text-[#6a766e] mt-1">Manage your challenges and matches</p>
      </div>

      {/* SECTION: Score rejected — needs resubmission */}
      {rejectedScores.length > 0 && (
        <div data-testid="rejected-scores-section">
          <h2 className="font-serif text-xl text-[#013523] mb-3 flex items-center gap-2">
            <Warning size={20} weight="duotone" className="text-red-500" />
            Score Rejected — Resubmit
          </h2>
          <div className="space-y-3">
            {rejectedScores.map(({ challenge: ch, match: m }) => {
              const isInvolved = ch.challenger_id === myPlayer?.player_id || ch.challenged_id === myPlayer?.player_id;
              return (
                <div key={ch.challenge_id} className="bg-red-50 rounded-2xl border border-red-200 p-4" data-testid={`rejected-match-${ch.challenge_id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-sm font-medium text-[#013523]">
                        <MemberLink profileId={ch.challenger_id} name={ch.challenger_name} /> vs <MemberLink profileId={ch.challenged_id} name={ch.challenged_name} />
                      </div>
                      <div className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <X size={12} weight="bold" />
                        {isWalkover(m.score) ? (
                          <>Previous result <span className="font-mono font-semibold">WALKOVER</span> was rejected</>
                        ) : (
                          <>Previous score <span className="font-mono">{formatScore(m.score)}</span> was rejected</>
                        )}
                      </div>
                      <div className="text-xs text-[#6a766e] mt-0.5">
                        Either player can submit a new score
                      </div>
                    </div>
                    <StatusPill status="rejected" testId="match-status-rejected" />
                  </div>
                  {isInvolved && (
                    <button
                      data-testid={`resubmit-score-${ch.challenge_id}`}
                      onClick={() => setSubmitModal(ch)}
                      className="w-full mt-2 bg-[#015e3f] text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
                    >
                      <ArrowsClockwise size={16} weight="bold" /> Submit New Score
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SECTION: Pending score approval */}
      {pendingScoreApproval.length > 0 && (
        <div data-testid="pending-matches-section">
          <h2 className="font-serif text-xl text-[#013523] mb-3 flex items-center gap-2">
            <Trophy size={20} weight="duotone" className="text-[#c2a251]" />
            Awaiting Score Approval
          </h2>
          <div className="space-y-3">
            {pendingScoreApproval.map(({ challenge: ch, match: m }) => {
              const canApprove = m.submitted_by !== myPlayer?.player_id &&
                (m.player1_id === myPlayer?.player_id || m.player2_id === myPlayer?.player_id);
              const iSubmitted = m.submitted_by === myPlayer?.player_id;
              const winnerName = m.winner_id === m.player1_id ? m.player1_name : m.player2_name;
              return (
                <div key={m.match_id} className="bg-[#c2a251]/5 rounded-2xl border border-[#c2a251]/30 p-4 relative" data-testid={`pending-match-${m.match_id}`}>
                  {/* Card-level "Updating…" only when the score modal just closed
                      and the refetch is still in flight. Approve button keeps
                      its own button-level state (handler does not setRefreshing). */}
                  {isCardRefreshing(ch.challenge_id) && <CardRefreshOverlay />}
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-sm font-medium text-[#013523]">
                        <MemberLink profileId={m.player1_id} name={m.player1_name} /> vs <MemberLink profileId={m.player2_id} name={m.player2_name} />
                      </div>
                      <div className="text-xs text-[#6a766e] mt-1">
                        {isWalkover(m.score) ? (
                          <>Result: <span className="font-mono font-semibold text-[#013523]">WALKOVER</span> — Winner: {winnerName}</>
                        ) : (
                          <>Score: <span className="font-mono">{formatScore(m.score)}</span> — Winner: {winnerName}</>
                        )}
                      </div>
                      {iSubmitted && (
                        <div className="text-xs text-[#c2a251] mt-0.5">You submitted this score. Waiting for opponent.</div>
                      )}
                    </div>
                    <StatusPill status="review" testId="match-status-pending" />
                  </div>
                  {canApprove && (
                    <div className="flex gap-2 mt-3">
                      <button
                        data-testid={`approve-match-${m.match_id}`}
                        onClick={() => handleApprove(m.match_id)}
                        disabled={isBusy(`approve:${m.match_id}`) || isBusy(`correct:${ch.challenge_id}`)}
                        className="flex-1 bg-[#015e3f] text-white rounded-full px-4 py-2 text-sm font-medium flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-70 disabled:active:scale-100"
                      >
                        <Check size={16} weight="bold" /> {isBusy(`approve:${m.match_id}`) ? 'Approving…' : 'Approve'}
                      </button>
                      <button
                        data-testid={`correct-match-${m.match_id}`}
                        onClick={() => handleCorrect(ch)}
                        disabled={isBusy(`approve:${m.match_id}`) || isBusy(`correct:${ch.challenge_id}`)}
                        className="flex-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-4 py-2 text-sm font-medium flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-70 disabled:active:scale-100"
                      >
                        <PencilSimple size={16} weight="bold" /> {isBusy(`correct:${ch.challenge_id}`) ? 'Opening correction…' : 'Correct Score'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SECTION: Accepted challenges awaiting score */}
      {awaitingScore.length > 0 && (
        <div data-testid="awaiting-score-section">
          <h2 className="font-serif text-xl text-[#013523] mb-3 flex items-center gap-2">
            <Lightning size={20} weight="duotone" className="text-[#015e3f]" />
            Ready for Score Entry
          </h2>
          <div className="space-y-3">
            {awaitingScore.map((ch) => (
              <ChallengeCard
                key={ch.challenge_id}
                challenge={ch}
                matchState="awaiting_score"
                myPlayer={myPlayer}
                onCancel={handleCancel}
                onSubmitScore={() => setSubmitModal(ch)}
                isBusy={isBusy}
                isCardRefreshing={isCardRefreshing}
              />
            ))}
          </div>
        </div>
      )}

      {/* SECTION: Pending challenges (not yet accepted) */}
      {pendingChallenges.length > 0 && (
        <div data-testid="pending-challenges-section">
          <h2 className="font-serif text-xl text-[#013523] mb-3 flex items-center gap-2">
            <Clock size={20} weight="duotone" className="text-[#c2a251]" />
            Pending Response
          </h2>
          <div className="space-y-3">
            {pendingChallenges.map((ch) => (
              <ChallengeCard
                key={ch.challenge_id}
                challenge={ch}
                matchState="none"
                myPlayer={myPlayer}
                onAccept={handleAccept}
                onDecline={handleDecline}
                onCancel={handleCancel}
                isBusy={isBusy}
                isCardRefreshing={isCardRefreshing}
              />
            ))}
          </div>
        </div>
      )}

      {!hasActiveItems && (
        <div className="text-center py-16">
          <Lightning size={48} weight="duotone" className="text-[#e3dcd1] mx-auto mb-4" />
          <p className="text-[#6a766e] font-serif text-xl">No active challenges</p>
          <p className="text-sm text-[#6a766e] mt-1">Go to the ladder to challenge a player</p>
        </div>
      )}

      {/* Past Challenges */}
      {pastChallenges.length > 0 && (
        <div>
          <h2 className="font-serif text-xl text-[#013523] mb-3 flex items-center gap-2">
            <Clock size={20} weight="duotone" className="text-[#6a766e]" />
            History
          </h2>
          <div className="space-y-2">
            {pastChallenges.slice(0, 10).map((ch) => {
              const match = matchByChallenge[ch.challenge_id];
              const isCompleted = ch.status === 'completed' && match;
              const winnerName = isCompleted
                ? (match.winner_id === match.player1_id ? match.player1_name : match.player2_name) ||
                  (match.winner_id === ch.challenger_id ? ch.challenger_name : ch.challenged_name)
                : null;
              const completedAt = isCompleted ? (match.updated_at || match.created_at || ch.updated_at) : null;
              const completedDate = completedAt
                ? new Date(completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                : null;
              const walkoverMatch = isCompleted && isWalkover(match.score);
              return (
                <div
                  key={ch.challenge_id}
                  className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] px-4 py-3"
                  data-testid={`past-challenge-${ch.challenge_id}`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-[#2a332c] truncate">
                        <MemberLink profileId={ch.challenger_id} name={ch.challenger_name} /> <span className="text-[#6a766e] font-normal">vs</span> <MemberLink profileId={ch.challenged_id} name={ch.challenged_name} />
                      </div>
                      {isCompleted ? (
                        <div className="text-xs text-[#6a766e] mt-1 flex items-center gap-1.5 flex-wrap">
                          <Trophy size={12} weight="fill" className="text-[#c2a251] shrink-0" />
                          <span className="text-[#013523] font-medium">{winnerName}</span>
                          <span className="text-[#6a766e]">·</span>
                          {walkoverMatch ? (
                            <span
                              className="font-mono font-semibold text-[#c2a251] tracking-wide"
                              data-testid={`past-challenge-walkover-${ch.challenge_id}`}
                            >
                              WALKOVER
                            </span>
                          ) : (
                            <span className="font-mono text-[#013523]">{formatScore(match.score)}</span>
                          )}
                          {completedDate && (
                            <>
                              <span className="text-[#6a766e]">·</span>
                              <span>{completedDate}</span>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-[#6a766e] mt-1 italic">
                          {ch.status === 'expired' && 'Challenge expired'}
                          {ch.status === 'canceled' && 'Canceled'}
                          {ch.status === 'declined' && 'Declined'}
                          {!['expired', 'canceled', 'declined'].includes(ch.status) && ch.status}
                        </div>
                      )}
                    </div>
                    <StatusPill
                      status={ch.status}
                      testId={`past-challenge-status-${ch.challenge_id}`}
                      className="shrink-0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Submit Score Modal */}
      {submitModal && (
        <ScoreModal
          challenge={submitModal}
          myPlayer={myPlayer}
          onClose={() => setSubmitModal(null)}
          onSubmitted={async () => {
            // Modal-close path: button label disappears with the modal, so
            // surface a subtle card overlay while the refetch runs. The
            // overlay clears as soon as the data arrives.
            const cid = submitModal.challenge_id;
            setSubmitModal(null);
            setRefreshing(cid, true);
            try {
              await fetchData();
            } finally {
              setRefreshing(cid, false);
            }
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel}
        destructive={confirmState?.destructive}
        onConfirm={runConfirm}
        onCancel={() => setConfirmState(null)}
        testId={`confirm-${confirmState?.kind || 'dialog'}`}
      />

    </div>
  );
}

function ChallengeCard({ challenge: ch, matchState, myPlayer, onAccept, onDecline, onCancel, onSubmitScore, isBusy, isCardRefreshing }) {
  const isChallenger = ch.challenger_id === myPlayer?.player_id;
  const isChallenged = ch.challenged_id === myPlayer?.player_id;
  const cid = ch.challenge_id;
  const acceptBusy = isBusy ? isBusy(`accept:${cid}`) : false;
  const declineBusy = isBusy ? isBusy(`decline:${cid}`) : false;
  const cancelBusy = isBusy ? isBusy(`cancel:${cid}`) : false;
  const cardRefreshing = isCardRefreshing ? isCardRefreshing(cid) : false;
  // While any action is in-flight, every action button on this card is
  // disabled to prevent racing taps (e.g. tapping Decline mid-Accept).
  const anyBusy = acceptBusy || declineBusy || cancelBusy;

  const getTimeLeft = () => {
    // Post-launch: accepted challenges no longer expire. Only the pending
    // 72h acceptance window still counts down.
    if (ch.status !== 'pending') return '';
    const deadline = ch.expires_at;
    if (!deadline) return '';
    const diff = new Date(deadline) - new Date();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h left`;
    return `${hours}h left`;
  };

  return (
    <div data-testid={`challenge-card-${ch.challenge_id}`}
         className="bg-[#fbfaf8] rounded-2xl border border-[#e3dcd1] p-4 transition-all hover:shadow-sm relative">
      {cardRefreshing && <CardRefreshOverlay />}
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="text-sm font-medium text-[#013523]">
            #{ch.challenger_rank} <MemberLink profileId={ch.challenger_id} name={ch.challenger_name} />
            <span className="text-[#6a766e] font-normal"> vs </span>
            #{ch.challenged_rank} <MemberLink profileId={ch.challenged_id} name={ch.challenged_name} />
          </div>
          <div className="flex items-center gap-2 mt-1">
            {ch.status === 'pending' && (
              <>
                <Clock size={14} className="text-[#6a766e]" />
                <span className="text-xs text-[#6a766e]">{getTimeLeft()}</span>
              </>
            )}
          </div>
        </div>
        <StatusPill
          status={ch.status}
          matchState={matchState}
          testId={`challenge-status-${ch.challenge_id}`}
        />
      </div>

      <div className="flex gap-2 mt-3">
        {/* Pending challenge: challenged can accept/decline */}
        {ch.status === 'pending' && isChallenged && (
          <>
            <button
              data-testid={`accept-challenge-${ch.challenge_id}`}
              onClick={() => onAccept(ch.challenge_id)}
              disabled={anyBusy}
              className="flex-1 bg-[#015e3f] text-white rounded-full px-4 py-2 text-sm font-medium flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-70 disabled:active:scale-100"
            >
              <Check size={16} weight="bold" /> {acceptBusy ? 'Accepting…' : 'Accept'}
            </button>
            <button
              data-testid={`decline-challenge-${ch.challenge_id}`}
              onClick={() => onDecline(ch.challenge_id)}
              disabled={anyBusy}
              className="flex-1 bg-red-50 text-red-600 border border-red-200 rounded-full px-4 py-2 text-sm font-medium flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-70 disabled:active:scale-100"
            >
              <X size={16} weight="bold" /> {declineBusy ? 'Declining…' : 'Decline'}
            </button>
          </>
        )}
        {/* Pending: challenger can cancel */}
        {ch.status === 'pending' && isChallenger && (
          <button
            data-testid={`cancel-challenge-${ch.challenge_id}`}
            onClick={() => onCancel(ch.challenge_id)}
            disabled={anyBusy}
            className="flex-1 bg-[#f0ece3] text-[#013523] border border-[#e3dcd1] rounded-full px-4 py-2 text-sm font-medium active:scale-95 transition-transform disabled:opacity-70 disabled:active:scale-100"
          >
            {cancelBusy ? 'Cancelling…' : 'Cancel Challenge'}
          </button>
        )}
        {/* Accepted + awaiting score: either can submit score or cancel */}
        {ch.status === 'accepted' && matchState === 'awaiting_score' && (isChallenger || isChallenged) && (
          <>
            <button
              data-testid={`submit-score-${ch.challenge_id}`}
              onClick={onSubmitScore}
              disabled={cancelBusy}
              className="flex-1 bg-[#015e3f] text-white rounded-full px-4 py-2 text-sm font-medium flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-70 disabled:active:scale-100"
            >
              <Trophy size={16} weight="bold" /> Submit Score
            </button>
            <button
              data-testid={`cancel-accepted-${ch.challenge_id}`}
              onClick={() => onCancel(ch.challenge_id)}
              disabled={cancelBusy}
              className="bg-[#f0ece3] text-[#013523] border border-[#e3dcd1] rounded-full px-4 py-2 text-sm font-medium active:scale-95 transition-transform disabled:opacity-70 disabled:active:scale-100"
            >
              {cancelBusy ? <ArrowsClockwise size={16} weight="bold" className="animate-spin" /> : <X size={16} weight="bold" />}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Subtle overlay rendered on top of a card while a refetch is in flight.
 * Intentionally light: tiny spinner + "Updating…" text. Not a blocking modal.
 */
function CardRefreshOverlay() {
  return (
    <div
      data-testid="card-refreshing-overlay"
      className="absolute inset-0 bg-white/60 backdrop-blur-[1px] rounded-2xl flex items-center justify-center gap-2 pointer-events-none"
    >
      <ArrowsClockwise size={14} weight="bold" className="text-[#015e3f] animate-spin" />
      <span className="text-xs text-[#013523] font-medium">Updating…</span>
    </div>
  );
}

function ScoreModal({ challenge, myPlayer, onClose, onSubmitted }) {
  const isCorrection = !!challenge.isCorrection;
  const [winnerId, setWinnerId] = useState('');
  const [walkover, setWalkover] = useState(false);
  const [sets, setSets] = useState([
    { a: '', b: '', tb: '' }, // Set 1
    { a: '', b: '', tb: '' }, // Set 2
  ]);
  const [mtb, setMtb] = useState({ a: '', b: '' }); // match tiebreak (3rd set)
  const [loading, setLoading] = useState(false);

  const updateSet = (idx, field, raw) => {
    // Keep only digits, max 2 chars for sets; tb allows up to 3.
    const cleaned = raw.replace(/[^0-9]/g, '').slice(0, field === 'tb' ? 3 : 2);
    setSets((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: cleaned } : s)));
  };
  const updateMtb = (field, raw) => {
    const cleaned = raw.replace(/[^0-9]/g, '').slice(0, 3);
    setMtb((prev) => ({ ...prev, [field]: cleaned }));
  };

  // -------- Validation helpers --------
  // Returns { valid, winner: 'a'|'b'|null, needsTiebreak, error }
  const validateSet = (s) => {
    if (s.a === '' || s.b === '') return { valid: false, winner: null, needsTiebreak: false };
    const a = parseInt(s.a, 10);
    const b = parseInt(s.b, 10);
    if (isNaN(a) || isNaN(b)) return { valid: false, winner: null, needsTiebreak: false, error: 'Invalid numbers' };
    const legalPairs = [
      [6, 0], [6, 1], [6, 2], [6, 3], [6, 4],
      [7, 5], [7, 6],
    ];
    let winner = null;
    let needsTiebreak = false;
    const match = (x, y) => legalPairs.find(([p, q]) => p === x && q === y);
    if (match(a, b)) {
      winner = 'a';
      needsTiebreak = a === 7 && b === 6;
    } else if (match(b, a)) {
      winner = 'b';
      needsTiebreak = b === 7 && a === 6;
    } else {
      return { valid: false, winner: null, needsTiebreak: false, error: `${a}-${b} is not a valid set score` };
    }
    if (needsTiebreak) {
      if (s.tb === '') return { valid: false, winner, needsTiebreak, error: 'Enter tiebreak loser points' };
      const tb = parseInt(s.tb, 10);
      if (isNaN(tb) || tb < 0 || tb > 20) {
        return { valid: false, winner, needsTiebreak, error: 'Invalid tiebreak points' };
      }
    }
    return { valid: true, winner, needsTiebreak };
  };

  const validateMtb = () => {
    if (mtb.a === '' || mtb.b === '') return { valid: false, winner: null };
    const a = parseInt(mtb.a, 10);
    const b = parseInt(mtb.b, 10);
    if (isNaN(a) || isNaN(b)) return { valid: false, winner: null, error: 'Invalid numbers' };
    const max = Math.max(a, b);
    const min = Math.min(a, b);
    if (max < 10) return { valid: false, winner: null, error: 'Match tiebreak requires ≥ 10' };
    if (max - min < 2) return { valid: false, winner: null, error: 'Must win by 2' };
    return { valid: true, winner: a > b ? 'a' : 'b' };
  };

  // -------- Derive match state (non-walkover) --------
  const setResults = sets.map(validateSet);
  const s1 = setResults[0];
  const s2 = setResults[1];
  // a = challenger, b = challenged
  let setsWonA = 0, setsWonB = 0;
  if (s1.valid) { if (s1.winner === 'a') setsWonA++; else setsWonB++; }
  if (s2.valid) { if (s2.winner === 'a') setsWonA++; else setsWonB++; }

  const needMatchTiebreak = s1.valid && s2.valid && setsWonA === 1 && setsWonB === 1;
  const mtbResult = needMatchTiebreak ? validateMtb() : { valid: true, winner: null };

  let computedWinner = null; // 'a' | 'b' | null
  if (!walkover) {
    if (s1.valid && s2.valid && setsWonA === 2) computedWinner = 'a';
    else if (s1.valid && s2.valid && setsWonB === 2) computedWinner = 'b';
    else if (needMatchTiebreak && mtbResult.valid) computedWinner = mtbResult.winner;
  }

  // First error-to-show (for inline help)
  const scoreError = (() => {
    if (walkover) return '';
    if (s1.error) return `Set 1: ${s1.error}`;
    if (s2.error) return `Set 2: ${s2.error}`;
    if (needMatchTiebreak && mtbResult.error) return `Match tiebreak: ${mtbResult.error}`;
    return '';
  })();

  // Winner-matches-score check (only when everything else is valid)
  const winnerRole = winnerId === challenge.challenger_id ? 'a' : winnerId === challenge.challenged_id ? 'b' : null;
  const winnerMismatch = !walkover && computedWinner && winnerRole && computedWinner !== winnerRole;

  const canSubmit = (() => {
    if (!winnerId || loading) return false;
    if (walkover) return true;
    if (!computedWinner) return false;
    if (winnerMismatch) return false;
    return true;
  })();

  // Serialize to display string for backend
  const serializeScore = () => {
    if (walkover) return WALKOVER;
    const formatSet = (s) => {
      const a = parseInt(s.a, 10);
      const b = parseInt(s.b, 10);
      const base = `${a}-${b}`;
      if ((a === 7 && b === 6) || (a === 6 && b === 7)) {
        return `${base} (${parseInt(s.tb, 10)})`;
      }
      return base;
    };
    const parts = [formatSet(sets[0]), formatSet(sets[1])];
    if (needMatchTiebreak) parts.push(`${parseInt(mtb.a, 10)}-${parseInt(mtb.b, 10)}`);
    return parts.join(', ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      if (!winnerId) toast.error('Please select a winner');
      else if (winnerMismatch) toast.error('Winner does not match the entered score');
      else if (scoreError) toast.error(scoreError);
      else toast.error('Complete the score entry');
      return;
    }
    setLoading(true);
    try {
      await matchAPI.submit(challenge.challenge_id, winnerId, serializeScore());
      const verb = isCorrection ? 'Corrected score' : (walkover ? 'Walkover' : 'Score');
      toast.success(`${verb} submitted! Waiting for opponent approval.`);
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  // Lock body scroll while the modal is open so the page underneath
  // doesn't scroll on touch devices. Restored on unmount.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        data-testid="score-submit-modal"
        className="w-full max-w-md bg-[#fbfaf8] rounded-t-2xl sm:rounded-2xl border border-[#e3dcd1] flex flex-col animate-fade-in-up max-h-[100dvh] sm:max-h-[90vh] sm:my-8"
      >
        {/* Header — pinned top */}
        <div className="flex justify-between items-center shrink-0 px-6 pt-6 pb-3">
          <h2 className="font-serif text-2xl text-[#013523]" data-testid="score-modal-title">
            {isCorrection ? 'Submit Corrected Score' : 'Submit Score'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[#6a766e] hover:text-[#013523]"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
        {isCorrection && (
          <p className="text-xs text-amber-700 bg-amber-50 border-y border-amber-200 px-6 py-2 shrink-0" data-testid="correction-banner">
            Your corrected score will replace the submitted one and be sent back to the other player for approval.
          </p>
        )}

        <div className="text-sm text-[#6a766e] shrink-0 px-6 pb-3">
          {challenge.challenger_name} (#{challenge.challenger_rank}) vs {challenge.challenged_name} (#{challenge.challenged_rank})
        </div>

        {/* Scrollable body — fields live here so the footer stays visible */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-4 space-y-4">
          {/* Winner selector */}
          <div>
            <label className="text-xs font-sans tracking-[0.1em] uppercase text-[#6a766e] mb-2 block">Winner</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="select-winner-challenger"
                onClick={() => setWinnerId(challenge.challenger_id)}
                className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                  winnerId === challenge.challenger_id
                    ? 'border-[#015e3f] bg-[#015e3f]/10 text-[#015e3f]'
                    : 'border-[#e3dcd1] text-[#2a332c] hover:border-[#015e3f]/30'
                }`}
              >
                {challenge.challenger_name}
              </button>
              <button
                type="button"
                data-testid="select-winner-challenged"
                onClick={() => setWinnerId(challenge.challenged_id)}
                className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                  winnerId === challenge.challenged_id
                    ? 'border-[#015e3f] bg-[#015e3f]/10 text-[#015e3f]'
                    : 'border-[#e3dcd1] text-[#2a332c] hover:border-[#015e3f]/30'
                }`}
              >
                {challenge.challenged_name}
              </button>
            </div>
          </div>

          {/* Structured set entry */}
          <fieldset disabled={walkover} className={walkover ? 'opacity-40 pointer-events-none select-none' : ''}>
            {/* Column headers — players' short names above the dropdowns */}
            <div className="grid grid-cols-[1fr_auto_1fr_auto] items-end gap-2 mb-1 px-1">
              <div className="text-[10px] font-sans tracking-[0.1em] uppercase text-[#6a766e] text-center truncate">
                {challenge.challenger_name.split(' ')[0]}
              </div>
              <div aria-hidden="true" className="w-4" />
              <div className="text-[10px] font-sans tracking-[0.1em] uppercase text-[#6a766e] text-center truncate">
                {challenge.challenged_name.split(' ')[0]}
              </div>
              <div aria-hidden="true" className="w-14" />
            </div>

            <div className="space-y-2.5">
              {[0, 1].map((i) => {
                const s = sets[i];
                const result = setResults[i];
                const showTb = result.needsTiebreak || (s.a === '7' && s.b === '6') || (s.a === '6' && s.b === '7');
                const selectCls = "w-full bg-white border border-[#e3dcd1] rounded-lg px-2 py-2 text-[#2a332c] text-center font-mono text-base focus:outline-none focus:ring-2 focus:ring-[#015e3f]/20 focus:border-[#015e3f] appearance-none";
                return (
                  <div key={i} data-testid={`set-row-${i + 1}`} className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2">
                    <select
                      data-testid={`set-${i + 1}-a`}
                      value={s.a}
                      onChange={(e) => updateSet(i, 'a', e.target.value)}
                      aria-label={`Set ${i + 1} ${challenge.challenger_name} games`}
                      className={selectCls}
                    >
                      <option value="">–</option>
                      {[0, 1, 2, 3, 4, 5, 6, 7].map(n => <option key={n} value={String(n)}>{n}</option>)}
                    </select>
                    <span className="text-[10px] font-sans tracking-[0.15em] uppercase text-[#6a766e] w-4 text-center">
                      S{i + 1}
                    </span>
                    <select
                      data-testid={`set-${i + 1}-b`}
                      value={s.b}
                      onChange={(e) => updateSet(i, 'b', e.target.value)}
                      aria-label={`Set ${i + 1} ${challenge.challenged_name} games`}
                      className={selectCls}
                    >
                      <option value="">–</option>
                      {[0, 1, 2, 3, 4, 5, 6, 7].map(n => <option key={n} value={String(n)}>{n}</option>)}
                    </select>
                    {showTb ? (
                      <input
                        data-testid={`set-${i + 1}-tb`}
                        type="text"
                        inputMode="numeric"
                        value={s.tb}
                        onChange={(e) => updateSet(i, 'tb', e.target.value)}
                        placeholder="TB"
                        aria-label={`Set ${i + 1} tiebreak loser points`}
                        className="w-14 bg-[#c2a251]/10 border border-[#c2a251]/40 rounded-lg px-1.5 py-2 text-[#013523] text-center font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#c2a251]/40"
                      />
                    ) : (
                      <div aria-hidden="true" className="w-14" />
                    )}
                  </div>
                );
              })}

              {(setResults[0].needsTiebreak || setResults[1].needsTiebreak) && (
                <div className="text-[10px] text-[#6a766e] px-1">
                  TB: enter loser's points (e.g. 4 → 7-6 (4))
                </div>
              )}

              {needMatchTiebreak && (
                <div data-testid="match-tiebreak-row" className="pt-1">
                  <div className="text-[10px] font-sans tracking-[0.1em] uppercase text-[#c2a251] font-semibold mb-1 px-1">
                    Match Tiebreak
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2">
                    <select
                      data-testid="mtb-a"
                      value={mtb.a}
                      onChange={(e) => updateMtb('a', e.target.value)}
                      aria-label={`Match tiebreak ${challenge.challenger_name} points`}
                      className="w-full bg-white border border-[#c2a251]/40 rounded-lg px-2 py-2 text-[#013523] text-center font-mono text-base focus:outline-none focus:ring-2 focus:ring-[#c2a251]/40 appearance-none"
                    >
                      <option value="">–</option>
                      {Array.from({ length: 21 }, (_, n) => (
                        <option key={n} value={String(n)}>{n}</option>
                      ))}
                    </select>
                    <span aria-hidden="true" className="w-4" />
                    <select
                      data-testid="mtb-b"
                      value={mtb.b}
                      onChange={(e) => updateMtb('b', e.target.value)}
                      aria-label={`Match tiebreak ${challenge.challenged_name} points`}
                      className="w-full bg-white border border-[#c2a251]/40 rounded-lg px-2 py-2 text-[#013523] text-center font-mono text-base focus:outline-none focus:ring-2 focus:ring-[#c2a251]/40 appearance-none"
                    >
                      <option value="">–</option>
                      {Array.from({ length: 21 }, (_, n) => (
                        <option key={n} value={String(n)}>{n}</option>
                      ))}
                    </select>
                    <div aria-hidden="true" className="w-14" />
                  </div>
                  <div className="text-[10px] text-[#6a766e] mt-1 px-1">First to 10, win by 2</div>
                </div>
              )}
            </div>
          </fieldset>

          {/* Walkover checkbox */}
          <label className="flex items-center gap-3 cursor-pointer select-none bg-white border border-[#e3dcd1] rounded-xl px-4 py-3">
            <input
              data-testid="walkover-checkbox"
              type="checkbox"
              checked={walkover}
              onChange={(e) => setWalkover(e.target.checked)}
              className="h-4 w-4 accent-[#015e3f]"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-[#2a332c]">Record as Walkover</div>
              <div className="text-[11px] text-[#6a766e]">
                Opponent didn't play. Winner still needed; no score required.
              </div>
            </div>
          </label>

          {/* Error / preview */}
          {!walkover && scoreError && (
            <div className="text-xs text-red-600 flex items-center gap-1" data-testid="score-error">
              <Warning size={14} weight="bold" /> {scoreError}
            </div>
          )}
          {!walkover && winnerMismatch && (
            <div className="text-xs text-red-600 flex items-center gap-1" data-testid="winner-mismatch-error">
              <Warning size={14} weight="bold" /> Selected winner doesn't match the entered score
            </div>
          )}
          {walkover && winnerId && (
            <div className="text-xs text-[#013523] font-mono" data-testid="walkover-preview">
              Result: WALKOVER — Winner: {winnerId === challenge.challenger_id ? challenge.challenger_name : challenge.challenged_name}
            </div>
          )}
        </div>
        {/* /scrollable body */}

        {/* Sticky footer — Cancel + Submit always visible. Safe-area
            inset prevents the buttons from being clipped by the iOS
            home indicator on phones with rounded corners. */}
        <div
          className="shrink-0 border-t border-[#e3dcd1] bg-[#fbfaf8] px-6 pt-3 pb-4 flex gap-3"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        >
          <button
            data-testid="cancel-score-submit"
            type="button"
            onClick={onClose}
            className="flex-1 bg-white border border-[#e3dcd1] text-[#013523] rounded-full px-6 py-3 font-medium transition-transform active:scale-95 hover:bg-[#f0ece3]"
          >
            Cancel
          </button>
          <button
            data-testid="confirm-score-submit"
            type="submit"
            disabled={!canSubmit}
            className="flex-1 bg-[#015e3f] text-white rounded-full px-6 py-3 font-medium transition-transform active:scale-95 hover:bg-[#014d34] disabled:opacity-50"
          >
            {loading
              ? (isCorrection ? 'Submitting correction…' : 'Submitting…')
              : isCorrection ? 'Submit Corrected Score'
              : walkover ? 'Submit Walkover'
              : 'Submit Score'}
          </button>
        </div>
      </form>
    </div>
  );
}
